import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryCustomFields } from '~/data/customField';
import {
  useGetUserUtils,
  useMutationUpdateCurrentUserSettings,
  useQueryUserData,
} from '~/data/user';

import {
  setDashboard_filterGroups,
  setDashboard_filterRows,
  setDashboard_selectedAcceptState,
  setDashboard_selectedArticle,
  setDashboard_selectedArticleNumber,
  setDashboard_selectedCategory,
  setDashboard_selectedCostCenter,
  setDashboard_selectedCustomerNumber,
  setDashboard_selectedCustomFields,
  setDashboard_selectedFilterGroup,
  setDashboard_selectedFromSite,
  setDashboard_selectedOuId,
  setDashboard_selectedPermittedCostCenters,
  setDashboard_selectedPermittedToSites,
  setDashboard_selectedProcessState,
  setDashboard_selectedRecipient,
  setDashboard_selectedSettledStatus,
  setDashboard_selectedSupplier,
  setDashboard_selectedToSiteRecipient,
  setDashboard_selectedToSiteSupplier,
  setDashboard_selectedToSiteSupplierTradeContact,
} from '~/redux/filtersSlice';
import { useDispatch, useSelector } from '~/redux/store';

import ToastService from '~/services/toast.service';

import { DeliveryNoteObject } from '~/models/deliveries/DeliveryNote';
import { FilterGroupFilter } from '~/models/filters/FilterGroupFilter';
import FilterNew from '~/models/filters/FilterNew';

import { clone, updateByKey } from '@vestigas/utils';
import { getFilterContext } from '~/utils/filters';
import { log } from '~/utils/logging';

import { withErrorBoundary } from '~/ui/atoms';
import { FilterGroups } from '~/ui/molecules/FilterGroups';

import { selectDashboardFilters } from '../utils';

import {
  emptyDashboardFilterGroup,
  getFilterGroupObject as getFilterGroupObjectUtil,
} from './utils/filterGroups';

export const DashboardFilterGroups = withErrorBoundary(({ onChangeValue }) => {
  const dispatch = useDispatch();
  const { UserUtils } = useGetUserUtils();

  const {
    dateRange,
    filterGroups,
    filterRows,
    selectedAcceptState,
    selectedArticle,
    selectedArticleNumber,
    selectedCategory,
    selectedCostCenter,
    selectedCustomerNumber,
    selectedCustomFields,
    selectedFilterGroup,
    selectedFromSite,
    selectedOuId,
    selectedPermittedCostCenters,
    selectedPermittedToSites,
    selectedProcessState,
    selectedRecipient,
    selectedSettledStatus,
    selectedSupplier,
    selectedToSiteRecipient,
    selectedToSiteSupplier,
    selectedToSiteSupplierTradeContact,
  } = useSelector(selectDashboardFilters);

  const { data: customFields } = useQueryCustomFields();

  const { data: currentUser } = useQueryUserData(true);
  const featureFlags = currentUser?.featureFlags ?? {};

  const { mutateAsync: updateUserSettings } =
    useMutationUpdateCurrentUserSettings();

  const [state, setState] = useState({
    filterGroups: [],
    selectableFilters: [],
  });

  const getFilterGroups = () => {
    const newFilterGroups = clone(filterGroups)
      .map((filterGroup) => {
        const activeFilters = FilterNew.getActiveFiltersForFilterGroup(
          filterGroup,
          'dashboard',
        );

        const nonApplicableBackendFilters =
          FilterNew.getNonApplicableFilters(activeFilters);
        if (nonApplicableBackendFilters.length > 0) {
          log.info({
            message: `"${filterGroup.name}" has non-applicable backend filters!`,
            data: {
              nonApplicableBackendFilters,
              filterGroup,
            },
          });
        }

        return {
          ...filterGroup,
          disabled: false, // nonApplicableBackendFilters.length > 0,
        };
      })
      .filter(Boolean);

    return newFilterGroups;
  };

  const getSelectableFilters = () => {
    const canFilterBillingStatus = featureFlags?.disableInvoices === false;

    const selectableFilters = [
      {
        id: FilterGroupFilter.FILTER.SELECTED_ACCEPT_STATE,
        name: DeliveryNoteObject.PROPERTY.ACCEPT_STATE.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_ARTICLE,
        name: DeliveryNoteObject.PROPERTY.ARTICLE.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_ARTICLE_NUMBER,
        name: DeliveryNoteObject.PROPERTY.ARTICLE_NUMBER.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_CATEGORY,
        name: DeliveryNoteObject.PROPERTY.CATEGORY.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_COST_CENTER,
        name: DeliveryNoteObject.PROPERTY.COST_CENTER.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_CUSTOMER_NUMBER,
        name: DeliveryNoteObject.PROPERTY.CUSTOMER_NUMBER.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_FROM_SITE,
        name: DeliveryNoteObject.PROPERTY.FROM_SITE.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_OU_ID,
        name: DeliveryNoteObject.PROPERTY.ORGANIZATIONAL_UNIT.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_PROCESS_STATE,
        name: DeliveryNoteObject.PROPERTY.PROCESS_STATE.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_RECIPIENT,
        name: DeliveryNoteObject.PROPERTY.RECIPIENT.STRING,
      },
      canFilterBillingStatus
        ? {
            id: FilterGroupFilter.FILTER.SELECTED_SETTLED_STATUS,
            name: DeliveryNoteObject.PROPERTY.SETTLED_STATUS.STRING,
          }
        : undefined,
      {
        id: FilterGroupFilter.FILTER.SELECTED_SUPPLIER,
        name: DeliveryNoteObject.PROPERTY.SUPPLIER.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_TO_SITE_RECIPIENT,
        name: DeliveryNoteObject.PROPERTY.TO_SITE_RECIPIENT.STRING,
      },
      {
        id: FilterGroupFilter.FILTER.SELECTED_TO_SITE_SUPPLIER,
        name: DeliveryNoteObject.PROPERTY.TO_SITE_SUPPLIER.STRING,
      },
    ].filter(Boolean);

    if (
      featureFlags.accessPermittedSites ||
      UserUtils.isPermittedSiteAllowedUser()
    ) {
      selectableFilters.push(
        {
          id: FilterGroupFilter.FILTER.SELECTED_PERMITTED_TO_SITES,
          name: DeliveryNoteObject.PROPERTY.PERMITTED_TO_SITE_NAMES.STRING,
        },
        {
          id: FilterGroupFilter.FILTER.SELECTED_PERMITTED_COST_CENTERS,
          name: DeliveryNoteObject.PROPERTY.PERMITTED_COST_CENTER_NAMES.STRING,
        },
      );
    }

    if (customFields && customFields.length > 0) {
      for (const customField of customFields) {
        selectableFilters.push({
          customField,
          disabled: false,
          id: customField.key,
          name: customField.displayName,
          type: customField.type,
        });
      }
    }

    return selectableFilters;
  };

  const getFilterGroupObject = useCallback(
    (
      id: string,
      name?: string,
      filterRows?: any[],
      isEmptyFilterGroup?: boolean,
    ) => {
      return getFilterGroupObjectUtil({
        filterGroupId: id,
        filterGroupName: name,
        filterGroups,
        filterRows,
        filterValues: {
          selectedAcceptState,
          selectedArticle,
          selectedArticleNumber,
          selectedCategory,
          selectedCostCenter,
          selectedCustomerNumber,
          selectedCustomFields,
          selectedFromSite,
          selectedOuId,
          selectedPermittedCostCenters,
          selectedPermittedToSites,
          selectedProcessState,
          selectedRecipient,
          selectedSettledStatus,
          selectedSupplier,
          selectedToSiteRecipient,
          selectedToSiteSupplier,
          selectedToSiteSupplierTradeContact,
        },
        isEmptyFilterGroup: isEmptyFilterGroup ?? false,
      });
    },
    [
      filterGroups,
      selectedAcceptState,
      selectedArticle,
      selectedArticleNumber,
      selectedCategory,
      selectedCostCenter,
      selectedCustomerNumber,
      selectedCustomFields,
      selectedFromSite,
      selectedOuId,
      selectedPermittedCostCenters,
      selectedPermittedToSites,
      selectedProcessState,
      selectedRecipient,
      selectedSettledStatus,
      selectedSupplier,
      selectedToSiteRecipient,
      selectedToSiteSupplier,
      selectedToSiteSupplierTradeContact,
    ],
  );

  const initSelectableFilters = () => {
    setState((previousState) => ({
      ...previousState,
      selectableFilters: getSelectableFilters(),
    }));
  };

  const resetFilterRows = () => {
    const firstSelectableFilter = getSelectableFilters()?.find(
      ({ disabled }) => !disabled,
    );
    const newFilterRows = [firstSelectableFilter?.id].filter(Boolean);

    handleUpdateFilterRows(newFilterRows);
  };

  const resetFilters = () => {
    dispatch(setDashboard_selectedAcceptState([]));
    dispatch(setDashboard_selectedArticle([]));
    dispatch(setDashboard_selectedArticleNumber([]));
    dispatch(setDashboard_selectedCategory([]));
    dispatch(setDashboard_selectedCostCenter([]));
    dispatch(setDashboard_selectedCustomFields([]));
    dispatch(setDashboard_selectedCustomerNumber([]));
    dispatch(setDashboard_selectedFromSite([]));
    dispatch(setDashboard_selectedOuId([]));
    dispatch(setDashboard_selectedPermittedCostCenters([]));
    dispatch(setDashboard_selectedPermittedToSites([]));
    dispatch(setDashboard_selectedProcessState([]));
    dispatch(setDashboard_selectedRecipient([]));
    dispatch(setDashboard_selectedSettledStatus([]));
    dispatch(setDashboard_selectedSupplier([]));
    dispatch(setDashboard_selectedToSiteRecipient([]));
    dispatch(setDashboard_selectedToSiteSupplier([]));
    dispatch(setDashboard_selectedToSiteSupplierTradeContact([]));

    resetFilterRows();
  };

  const initFilterGroups = () => {
    const newFilterGroups = getFilterGroups();

    setState((previousState) => ({
      ...previousState,
      filterGroups: newFilterGroups,
    }));

    if (
      newFilterGroups.find(({ id }) => id === selectedFilterGroup)?.disabled
    ) {
      dispatch(setDashboard_selectedFilterGroup(null));
      resetFilters();
    }
  };

  const handleClickFilterGroup = (id) => {
    if (id !== selectedFilterGroup) {
      resetFilters();
      dispatch(setDashboard_selectedFilterGroup(id));
      const filterGroup = filterGroups.find((group) => group.id === id);
      handleChangeFilterGroup(filterGroup);
      return;
    }

    dispatch(setDashboard_selectedFilterGroup(null));
    resetFilters();
  };

  const handleSaveNewFilterGroup = async (
    id,
    name,
    filterRows,
    emptyFilters,
  ) => {
    const newFilterGroups = [
      ...filterGroups,
      getFilterGroupObject(id, name, filterRows, emptyFilters),
    ];

    dispatch(setDashboard_filterGroups(newFilterGroups));
    dispatch(setDashboard_selectedFilterGroup(id));

    await updateUserSettings(
      { webappDashboardFilterGroups: newFilterGroups },
      {
        onError(error) {
          log.error({ message: 'Failed to save new filter group.', error });
          ToastService.error(['Filter konnte nicht hinzugefügt werden.']);
        },
      },
    );
  };

  const handleUpdateFilterGroupName = async (id, name) => {
    const newFilterGroups = clone(filterGroups);

    for (const filterGroup of newFilterGroups) {
      if (filterGroup.id === id) {
        filterGroup.name = name;
      }
    }

    dispatch(setDashboard_filterGroups(newFilterGroups));

    await updateUserSettings(
      { webappDashboardFilterGroups: newFilterGroups },
      {
        onError(error) {
          log.error({
            message: 'Failed to update filter group name.',
            error,
          });
          ToastService.error(['Filter konnte nicht umbenannt werden.']);
        },
      },
    );
  };

  const handleUpdateFilterGroup = async (filterRows) => {
    let newFilterGroups = clone(filterGroups);

    newFilterGroups = updateByKey(
      filterGroups,
      'id',
      selectedFilterGroup,
      getFilterGroupObject(selectedFilterGroup, null, filterRows),
    );

    dispatch(setDashboard_filterGroups(newFilterGroups));

    await updateUserSettings(
      { webappDashboardFilterGroups: newFilterGroups },
      {
        onError(error) {
          log.error({ message: 'Failed to update filter group.', error });
          ToastService.error(['Filter konnte nicht geändert werden.']);
        },
      },
    );
  };

  const handleUpdateFilterRows = (filterRows) => {
    dispatch(setDashboard_filterRows(filterRows));
  };

  const handleDeleteFilterGroup = async () => {
    let newFilterGroups = clone(filterGroups);

    newFilterGroups = filterGroups.filter(
      ({ id }) => id !== selectedFilterGroup,
    );

    dispatch(setDashboard_filterGroups(newFilterGroups));
    dispatch(setDashboard_selectedFilterGroup(null));
    resetFilters();

    await updateUserSettings(
      { webappDashboardFilterGroups: newFilterGroups },
      {
        onError(error) {
          log.error({ message: 'Failed to delete filter group.', error });
          ToastService.error(['Filter konnte nicht gelöscht werden.']);
        },
      },
    );
  };

  const handleChangeFilterGroup = (filterGroupRaw?: Record<string, any>) => {
    const filterGroup =
      filterGroupRaw ??
      filterGroups.find(({ id }) => id === selectedFilterGroup);

    if (!filterGroup) {
      return;
    }

    // Update filter rows
    dispatch(setDashboard_filterRows(filterGroup.filterRows || []));

    // Update all filter states
    dispatch(
      setDashboard_selectedAcceptState(
        filterGroup.filters?.selectedAcceptState ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedArticle(filterGroup.filters?.selectedArticle ?? []),
    );
    dispatch(
      setDashboard_selectedArticleNumber(
        filterGroup.filters?.selectedArticleNumber ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedCategory(
        filterGroup.filters?.selectedCategory ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedCostCenter(
        filterGroup.filters?.selectedCostCenter ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedCustomerNumber(
        filterGroup.filters?.selectedCustomerNumber ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedCustomFields(
        filterGroup.filters?.selectedCustomFields ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedFromSite(
        filterGroup.filters?.selectedFromSite ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedOuId(filterGroup.filters?.selectedOuId ?? []),
    );
    dispatch(
      setDashboard_selectedPermittedCostCenters(
        filterGroup.filters?.selectedPermittedCostCenters ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedPermittedToSites(
        filterGroup.filters?.selectedPermittedToSites ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedProcessState(
        filterGroup.filters?.selectedProcessState ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedRecipient(
        filterGroup.filters?.selectedRecipient ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedSettledStatus(
        filterGroup.filters?.selectedSettledStatus ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedSupplier(
        filterGroup.filters?.selectedSupplier ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedToSiteRecipient(
        filterGroup.filters?.selectedToSiteRecipient ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedToSiteSupplier(
        filterGroup.filters?.selectedToSiteSupplier ?? [],
      ),
    );
    dispatch(
      setDashboard_selectedToSiteSupplierTradeContact(
        filterGroup.filters?.selectedToSiteSupplierTradeContact ?? [],
      ),
    );
  };

  const initSelectedFilterGroup = () => {
    if (!selectedFilterGroup || filterGroups.length === 0) {
      const activeFilters = getActiveFilters();
      if (activeFilters.length > 0) {
        dispatch(setDashboard_filterRows(activeFilters));
      } else {
        resetFilterRows();
      }

      return;
    }

    const filterGroup = filterGroups.find(
      ({ id }) => id === selectedFilterGroup,
    );
    if (filterGroup) {
      handleChangeFilterGroup(filterGroup);
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];

    if (selectedAcceptState?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_ACCEPT_STATE);
    }

    if (selectedArticle?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_ARTICLE);
    }

    if (selectedArticleNumber?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_ARTICLE_NUMBER);
    }

    if (selectedCategory?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_CATEGORY);
    }

    if (selectedCostCenter?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_COST_CENTER);
    }

    if (selectedCustomerNumber?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_CUSTOMER_NUMBER);
    }

    if (selectedCustomFields?.length > 0) {
      for (const cf of selectedCustomFields) {
        if (cf.values?.length > 0) {
          activeFilters.push(cf.key);
        }
      }
    }

    if (selectedFromSite?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_FROM_SITE);
    }

    if (selectedOuId?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_OU_ID);
    }

    if (selectedPermittedCostCenters?.length > 0) {
      activeFilters.push(
        FilterGroupFilter.FILTER.SELECTED_PERMITTED_COST_CENTERS,
      );
    }

    if (selectedPermittedToSites?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_PERMITTED_TO_SITES);
    }

    if (selectedProcessState?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_PROCESS_STATE);
    }

    if (selectedRecipient?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_RECIPIENT);
    }

    if (selectedSettledStatus?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_SETTLED_STATUS);
    }

    if (selectedSupplier?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_SUPPLIER);
    }

    if (selectedToSiteRecipient?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_TO_SITE_RECIPIENT);
    }

    if (selectedToSiteSupplier?.length > 0) {
      activeFilters.push(FilterGroupFilter.FILTER.SELECTED_TO_SITE_SUPPLIER);
    }

    if (selectedToSiteSupplierTradeContact?.length > 0) {
      activeFilters.push(
        FilterGroupFilter.FILTER.SELECTED_TO_SITE_SUPPLIER_TRADE_CONTACT,
      );
    }

    return activeFilters;
  };

  const resetSelectedFilterGroup = () => {
    if (!filterGroups?.length) {
      dispatch(setDashboard_selectedFilterGroup(null));

      const activeFilters = getActiveFilters();

      if (activeFilters?.length) {
        dispatch(setDashboard_filterRows(activeFilters));
      } else {
        handleChangeFilterGroup();
      }

      return;
    }

    const newSelectedFilterGroup = selectedFilterGroup ?? filterGroups[0]?.id;

    dispatch(setDashboard_selectedFilterGroup(newSelectedFilterGroup));
    handleChangeFilterGroup(newSelectedFilterGroup);
  };

  useEffect(() => {
    initSelectedFilterGroup();
    initSelectableFilters();
    initFilterGroups();
  }, []);

  useEffect(() => {
    initSelectedFilterGroup();
  }, [filterGroups.length]);

  useEffect(() => {
    initFilterGroups();
  }, [filterGroups, dateRange]);

  useEffect(() => {
    initSelectableFilters();
  }, [
    dateRange,
    selectedAcceptState,
    selectedArticle,
    selectedArticleNumber,
    selectedCategory,
    selectedCostCenter,
    selectedCustomerNumber,
    selectedCustomFields,
    selectedFilterGroup,
    selectedFromSite,
    selectedOuId,
    selectedPermittedCostCenters,
    selectedPermittedToSites,
    selectedProcessState,
    selectedRecipient,
    selectedSettledStatus,
    selectedSupplier,
    selectedToSiteRecipient,
    selectedToSiteSupplier,
  ]);

  useEffect(() => {
    if (customFields) {
      initSelectableFilters();
    }
  }, [JSON.stringify(customFields)]);

  const currentFilterGroupObject = useMemo(
    () => getFilterGroupObject(selectedFilterGroup),
    [getFilterGroupObject, selectedFilterGroup],
  );

  const originalFilterGroupObject = useMemo(
    () => filterGroups.find(({ id }) => id === selectedFilterGroup),
    [filterGroups, selectedFilterGroup],
  );

  return (
    <FilterGroups
      currentFilterGroupObject={currentFilterGroupObject}
      deleteFilterGroup={handleDeleteFilterGroup}
      emptyFilterGroupObject={emptyDashboardFilterGroup}
      filterContext={getFilterContext({ page: 'dashboard' })}
      filterGroups={state.filterGroups}
      filterRows={filterRows}
      originalFilterGroupObject={originalFilterGroupObject}
      resetSelectedFilterGroup={resetSelectedFilterGroup}
      saveNewFilterGroup={handleSaveNewFilterGroup}
      selectableFilters={state.selectableFilters}
      selectedFilterGroup={selectedFilterGroup}
      updateFilterGroup={handleUpdateFilterGroup}
      updateFilterGroupName={handleUpdateFilterGroupName}
      updateFilterRows={handleUpdateFilterRows}
      filterGroupOpen
      onChangeValue={onChangeValue}
      onClickFilterGroup={handleClickFilterGroup}
    />
  );
}, 'Filtergruppen konnten nicht geladen werden.');

DashboardFilterGroups.displayName = 'DashboardFilterGroups';
